import { useState, useEffect, useRef, useCallback } from 'react'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''
const POLL_MS  = 12_000

const EMPTY_SET = new Set()

export function useNewEventToast({ enabled = true, silentCategories = EMPTY_SET } = {}) {
  const [toast, setToast]     = useState(null)
  const lastIdRef             = useRef(null)
  const initializedRef        = useRef(false)

  // Read through refs so toggling a setting never restarts the poller.
  const enabledRef = useRef(enabled)
  const silentRef  = useRef(silentCategories)
  useEffect(() => {
    enabledRef.current = enabled
    silentRef.current  = silentCategories
  })

  // Turning the popup off hides whatever is currently on screen.
  const [prevEnabled, setPrevEnabled] = useState(enabled)
  if (enabled !== prevEnabled) {
    setPrevEnabled(enabled)
    if (!enabled) setToast(null)
  }

  const poll = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/events?limit=1`)
      if (!res.ok) return
      const [event] = await res.json()
      if (!event) return

      if (!initializedRef.current) {
        lastIdRef.current    = event.id
        initializedRef.current = true
        return
      }

      if (event.id === lastIdRef.current) return

      // Always advance the cursor, even when muted — re-enabling the popup
      // must not replay events that happened while it was off.
      lastIdRef.current = event.id
      if (!enabledRef.current) return
      if (silentRef.current.has(event.category)) return
      setToast({ event, key: Date.now() })
    } catch {}
  }, [])

  useEffect(() => {
    poll()
    const id = setInterval(poll, POLL_MS)
    return () => clearInterval(id)
  }, [poll])

  const dismiss = useCallback(() => setToast(null), [])

  return { toast, dismiss }
}
